import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ContactPickItem, DocumentDto, SiteVisitDto, SiteVisitPatch } from '@tww/shared';
import { SITE_VISIT_SECTIONS, fmtCoords, fmtDate, fmtEur, parseCoords, sectionProgress, validateSiteVisitForFinalize } from '@tww/shared';
import { api, ApiRequestError, qs } from '../../api/client';
import { useLookups } from '../../api/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { ContactPicker } from '../../ui/ContactPicker';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ErrorState, Note, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { DateInput, Field, NumberInput, Select, TextArea, TextInput } from '../../ui/fields';
import { useToast } from '../../ui/Toast';
import { Dropdown } from '../../ui/Dropdown';

type Draft = SiteVisitPatch & { area: NonNullable<SiteVisitPatch['area']> };

/**
 * Begehungsprotokoll (Plan 03 Abschnitt 5.3): Abschnitte links, Formular rechts, Autosave alle 10 s und bei Abschnittswechsel,
 * Segment-Buttons für kurze Auswahlen, Geolocation für Koordinaten, Baumarten-Zeilen, Fotos, Abschluss mit Übernahme.
 */
export function SiteVisitPage() {
  const { id } = useParams();
  const vid = Number(id);
  const q = useQuery({ queryKey: ['visit', vid], queryFn: () => api.get<SiteVisitDto>(`/site-visits/${vid}`), enabled: Number.isFinite(vid), staleTime: Infinity });
  if (q.isLoading) return <Skeleton rows={8} />;
  if (q.isError || !q.data) return <ErrorState error={q.error ?? new Error('Protokoll nicht gefunden')} retry={() => q.refetch()} />;
  return <Editor key={vid} visit={q.data} />;
}

function toDraft(v: SiteVisitDto): Draft {
  const { id: _i, area, forester: _f, owner: _o, visited_by: _vb, action: _a, checklist_stub: _c, updated_at: _u, created_at: _cr, status: _s, finalized_at: _fa, area_id: _ai, ...fields } = v as any;
  const { id: _aid, name: _n, status: _as, state_code: _sc, region: _r, owner_name_text: _ont, ...areaFields } = area;
  return { ...fields, area: areaFields };
}

function Editor({ visit }: { visit: SiteVisitDto }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { can, me } = useAuth();
  const lookups = useLookups();
  const write = can('areas', 'write') && visit.status === 'draft';
  const [d, setD] = useState<Draft>(() => toDraft(visit));
  const [forester, setForester] = useState<ContactPickItem | null>(visit.forester ? { id: visit.forester.id, display_name: visit.forester.display_name, sub: null, rating_grade: visit.forester.rating_grade, rating_reason: null, city: null, region: null, last_price: visit.forester.last_price, categories: [] } : null);
  const [owner, setOwner] = useState<ContactPickItem | null>(visit.owner ? { id: visit.owner.id, display_name: visit.owner.display_name, sub: null, rating_grade: null, rating_reason: null, city: null, region: null, last_price: null, categories: [] } : null);
  const [section, setSection] = useState(SITE_VISIT_SECTIONS[0]!.key);
  const [coords, setCoords] = useState(fmtCoords(visit.area.lat, visit.area.lng));
  const [parking, setParking] = useState(fmtCoords(visit.parking_lat, visit.parking_lng));
  const [saveState, setSaveState] = useState<{ kind: 'saved' | 'dirty' | 'busy' | 'offline' | 'error'; at?: string; msg?: string }>({ kind: 'saved', at: visit.updated_at });
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [result, setResult] = useState<{ applied: string[]; warnings: string[] } | null>(null);
  const dirty = useRef<Partial<Draft>>({});
  const updatedAt = useRef(visit.updated_at);
  const timer = useRef<number | null>(null);

  const opts = (key: string) => (lookups.data?.find((l) => l.key === key)?.values ?? []).filter((v) => v.is_active).map((v) => ({ value: v.code, label: v.label }));
  const species = opts('tree_species');

  const flush = useCallback(async () => {
    if (!write) return;
    const patch = dirty.current;
    if (!Object.keys(patch).length) return;
    dirty.current = {};
    setSaveState({ kind: 'busy' });
    try {
      const r = await api.patch<SiteVisitDto>(`/site-visits/${visit.id}`, { ...patch, updated_at: updatedAt.current });
      updatedAt.current = r.updated_at;
      setSaveState({ kind: 'saved', at: r.updated_at });
      qc.setQueryData(['visit', visit.id], r);
    } catch (e) {
      dirty.current = { ...patch, ...dirty.current };
      if (e instanceof ApiRequestError && e.status === 409) setSaveState({ kind: 'error', msg: e.message });
      else if (!navigator.onLine) setSaveState({ kind: 'offline' });
      else setSaveState({ kind: 'error', msg: e instanceof Error ? e.message : 'Speichern fehlgeschlagen' });
    }
  }, [visit.id, write, qc]);

  const set = useCallback(
    (patch: Partial<Draft>) => {
      setD((s) => ({ ...s, ...patch, area: { ...s.area, ...(patch.area ?? {}) } }));
      const cur = dirty.current;
      dirty.current = { ...cur, ...patch, ...(patch.area ? { area: { ...(cur.area ?? {}), ...patch.area } } : {}) };
      setSaveState((s) => (s.kind === 'busy' ? s : { kind: 'dirty' }));
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(flush, 10_000);
    },
    [flush],
  );
  const setArea = (patch: Partial<Draft['area']>) => set({ area: patch } as Partial<Draft>);
  useEffect(() => {
    const on = () => flush();
    window.addEventListener('online', on);
    window.addEventListener('beforeunload', on);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('beforeunload', on);
      if (timer.current) window.clearTimeout(timer.current);
      flush();
    };
  }, [flush]);
  const goSection = (k: string) => {
    flush();
    setSection(k);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Abschnittsleiste auf dem Smartphone: aktiven Eintrag sichtbar halten
    window.setTimeout(() => document.querySelector('.proto .steps button.on')?.scrollIntoView({ block: 'nearest', inline: 'center' }), 50);
  };

  const validation = useMemo(() => validateSiteVisitForFinalize(d as any), [d]);
  const progress = (fields: string[]) => sectionProgress(d as any, fields);
  const sectionErr = (fields: string[]) => fields.some((f) => validation.errors[f]);

  // Abgeschlossenes Protokoll: nur Unterschriftsdaten sind noch änderbar, eigener Speicherweg mit Rückmeldung
  const amend = can('areas', 'write') && visit.status === 'final';
  const [signed, setSigned] = useState({ signed_forester_at: visit.signed_forester_at ?? null, signed_tww_at: visit.signed_tww_at ?? null });
  const signedDirty = signed.signed_forester_at !== (visit.signed_forester_at ?? null) || signed.signed_tww_at !== (visit.signed_tww_at ?? null);
  const saveSigned = useMutation({
    mutationFn: () => api.patch<SiteVisitDto>(`/site-visits/${visit.id}`, { ...signed, updated_at: updatedAt.current }),
    onSuccess: (r) => {
      updatedAt.current = r.updated_at;
      qc.setQueryData(['visit', visit.id], r);
      toast('Unterschriftsdaten gespeichert');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const finalize = useMutation({
    mutationFn: async () => {
      await flush();
      return api.post<{ visit: SiteVisitDto; applied: string[]; warnings: string[] }>(`/site-visits/${visit.id}/finalize`);
    },
    onSuccess: (r) => {
      qc.setQueryData(['visit', visit.id], r.visit);
      qc.invalidateQueries({ queryKey: ['area', visit.area.id] });
      qc.invalidateQueries({ queryKey: ['areas'] });
      setFinalizeOpen(false);
      setResult({ applied: r.applied, warnings: r.warnings });
    },
    onError: (e) => toast(e instanceof ApiRequestError ? [e.message, ...Object.values(e.fields ?? {})].join(' · ') : 'Abschluss fehlgeschlagen', 'error'),
  });

  const geo = (target: 'area' | 'parking') => {
    if (!navigator.geolocation) return toast('Standort auf diesem Gerät nicht verfügbar', 'error');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const lat = Math.round(p.coords.latitude * 1e6) / 1e6;
        const lng = Math.round(p.coords.longitude * 1e6) / 1e6;
        if (target === 'area') {
          setCoords(`${lat}, ${lng}`);
          setArea({ lat, lng });
        } else {
          setParking(`${lat}, ${lng}`);
          set({ parking_lat: lat, parking_lng: lng });
        }
        toast(`Standort übernommen (±${Math.round(p.coords.accuracy)} m)`);
      },
      () => toast('Standort konnte nicht ermittelt werden', 'error'),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  const ctx: Ctx = { d, set, write, opts, validation };
  const err = (k: string) => (write && validation.errors[k] ? validation.errors[k] : undefined);
  const speciesSum = (d.species ?? []).reduce((s, x) => s + (x.count || 0), 0);
  const priceHint = forester?.last_price ? `Letzter Preis dieses Försters: ${fmtEur(forester.last_price.price)} (${forester.last_price.year})${d.price_per_tree_max != null && Math.abs(d.price_per_tree_max - forester.last_price.price) >= 0.005 ? ' · weicht ab, wird als neuer Preis übernommen' : ''}` : 'Noch kein Preis beim Förster hinterlegt';

  return (
    <>
      <div className="crumb">
        <Link to="/flaechen">Flächen</Link> / <Link to={`/flaechen/${visit.area.id}`}>{visit.area.name}</Link>
      </div>
      <div className="head">
        <div>
          <h1>Begehungsprotokoll</h1>
          <div className="sub">
            {visit.area.name}
            {visit.area.region ? ` · ${visit.area.region}` : ''} · {visit.status === 'final' ? `abgeschlossen am ${fmtDate(visit.finalized_at)}` : 'Entwurf'}
          </div>
        </div>
        <div className="actions">
          <a className="btn" href={`/api/v1/site-visits/${visit.id}/print`} target="_blank" rel="noreferrer">
            <Icon name="doc" /> Druckansicht
          </a>
          {write && (
            <button className="btn primary" type="button" onClick={() => setFinalizeOpen(true)}>
              Protokoll abschließen
            </button>
          )}
        </div>
      </div>
      {write && (
        <div className="savebar">
          <span className={`dot ${saveState.kind === 'saved' ? '' : saveState.kind === 'busy' ? 'busy' : saveState.kind === 'offline' ? 'off' : saveState.kind === 'error' ? 'off' : 'dirty'}`} />
          {saveState.kind === 'saved' && `gespeichert ${saveState.at ? new Date(saveState.at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}`}
          {saveState.kind === 'dirty' && 'Änderungen werden in 10 Sekunden gespeichert'}
          {saveState.kind === 'busy' && 'wird gespeichert …'}
          {saveState.kind === 'offline' && 'offline, wird bei Verbindung hochgeladen'}
          {saveState.kind === 'error' && <span className="miss">{saveState.msg}</span>}
          <button type="button" className="btn sm" onClick={flush}>
            Speichern
          </button>
        </div>
      )}
      {visit.status === 'final' && <Note kind="good">Abgeschlossen. Übernommene Werte stehen in der Flächenakte, beim Förster und{visit.action ? ` in der Checkliste der Aktion ${visit.action.code}` : ' am Protokoll, bis eine Aktion verknüpft ist'}.</Note>}

      <div className="proto">
        <nav className="steps" aria-label="Abschnitte">
          {SITE_VISIT_SECTIONS.map((s, i) => {
            const p = progress(s.fields);
            const e = sectionErr(s.fields) && write;
            return (
              <button key={s.key} type="button" className={section === s.key ? 'on' : ''} onClick={() => goSection(s.key)}>
                <span>
                  {i + 1}. {s.title}
                </span>
                <span className={`n ${e ? 'err' : p.filled === p.total ? 'done' : ''}`}>{p.filled}/{p.total}</span>
              </button>
            );
          })}
          <button type="button" className={section === 'fotos' ? 'on' : ''} onClick={() => goSection('fotos')}>
            <span>Fotos</span>
          </button>
        </nav>

        <div className="col">
          {section === 'kopf' && (
            <section className="section">
              <div className="sh"><h2>Begehung</h2></div>
              <div className="sb">
                <div className="form">
                  <DateInput label="Begehung am" value={d.visited_at} onChange={(v) => set({ visited_at: v || null })} required error={err('visited_at')} disabled={!write} />
                  <TextInput label="Begehung durch" value={d.visited_by_user_id === me?.user.id ? me?.user.initials || me?.user.display_name || '' : visit.visited_by ?? d.visited_by_text ?? ''} onChange={(v) => set({ visited_by_text: v, visited_by_user_id: null })} hint="Kürzel der Person" disabled={!write} />
                  <div className="full">
                    <ContactPicker label="Förster" category="forester" required error={err('forester_contact_id')} value={forester} onChange={(c) => { setForester(c); set({ forester_contact_id: c?.id ?? null }); }} />
                  </div>
                  {visit.forester && (
                    <div className="full picker-info">
                      {[visit.forester.mobile ?? visit.forester.phone, visit.forester.email].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
          {section === 'flaeche' && (
            <section className="section">
              <div className="sh"><h2>Fläche</h2><span className="desc">wird in der Flächenakte gespeichert</span></div>
              <div className="sb">
                <div className="form">
                  <TextInput label="Ort der Pflanzfläche" value={d.area.city} onChange={(v) => setArea({ city: v })} required error={err('area.city')} disabled={!write} />
                  <TextInput label="Gemeinde" value={d.area.municipality} onChange={(v) => setArea({ municipality: v })} disabled={!write} />
                  <TextInput label="Gemarkung" value={d.area.district} onChange={(v) => setArea({ district: v })} disabled={!write} />
                  <div className="form three full">
                    <TextInput label="Flur" value={d.area.flur} onChange={(v) => setArea({ flur: v })} disabled={!write} />
                    <TextInput label="Flurstück" value={d.area.parcel} onChange={(v) => setArea({ parcel: v })} disabled={!write} />
                    <TextInput label="Abteilung" value={d.area.compartment} onChange={(v) => setArea({ compartment: v })} disabled={!write} />
                  </div>
                  <div className="geo-row full">
                    <TextInput label="Google-Maps-Koordinaten (Dezimalgrad)" value={coords} onChange={(v) => { setCoords(v); const c = parseCoords(v); setArea({ lat: c?.lat ?? null, lng: c?.lng ?? null }); }} placeholder="53.244, 9.962" disabled={!write} error={validation.warnings.find((w) => w.startsWith('Fläche'))} />
                    {write && <button type="button" className="btn" onClick={() => geo('area')}><Icon name="map" /> Aktuellen Standort übernehmen</button>}
                  </div>
                  <NumberInput label="Hektar" decimals={2} value={d.area.hectares ?? null} onChange={(v) => setArea({ hectares: v })} error={err('trees_planned') ? 'Hektar oder Anzahl Bäume angeben' : undefined} disabled={!write} />
                  <div>
                    <ContactPicker label="Waldbesitzer (Kontakt)" category="owner" value={owner} onChange={(c) => { setOwner(c); setArea({ owner_contact_id: c?.id ?? null }); if (c) set({ owner_name_text: c.display_name }); }} />
                  </div>
                  <T ctx={ctx} k="owner_name_text" label="Name Waldbesitzer (Text)" hint="Wenn noch kein Kontakt angelegt ist" />
                  <TextArea label="Ökologische Notwendigkeit / Schadereignis" value={d.area.ecological_need} onChange={(v) => setArea({ ecological_need: v })} rows={2} disabled={!write} />
                  <TextArea label="Weiteres Pflanzpotenzial auf Nachbarflächen" value={d.area.neighbour_potential} onChange={(v) => setArea({ neighbour_potential: v })} rows={2} disabled={!write} />
                </div>
              </div>
            </section>
          )}
          {section === 'pflanzung' && (
            <section className="section">
              <div className="sh"><h2>Pflanzung</h2></div>
              <div className="sb">
                <div className="form">
                  <NumberInput label="Anzahl Bäume" value={d.trees_planned ?? null} onChange={(v) => set({ trees_planned: v })} error={err('trees_planned')} disabled={!write} />
                  <N ctx={ctx} k="trees_by_participants" label="Geplante Pflanzenzahl durch Teilnehmende" />
                  <Field label="Baumarten mit Anzahl" full hint={validation.warnings.find((w) => w.startsWith('Summe'))}>
                    <div className="species-rows">
                      {(d.species ?? []).map((row, i) => (
                        <div className="r" key={i}>
                          <Dropdown value={row.species_code} onChange={(v) => set({ species: (d.species ?? []).map((x, j) => (j === i ? { ...x, species_code: v } : x)) })} disabled={!write} aria-label="Baumart" placeholder="Baumart" options={species.map((s) => ({ value: String(s.value), label: s.label }))} />
                          <input type="number" inputMode="numeric" value={row.count || ''} onChange={(e) => set({ species: (d.species ?? []).map((x, j) => (j === i ? { ...x, count: Number(e.target.value) || 0 } : x)) })} disabled={!write} aria-label="Anzahl" />
                          {write && <button type="button" className="icon-btn" aria-label="Zeile entfernen" onClick={() => set({ species: (d.species ?? []).filter((_, j) => j !== i) })}><Icon name="x" /></button>}
                        </div>
                      ))}
                      {write && <div><button type="button" className="btn sm" onClick={() => set({ species: [...(d.species ?? []), { species_code: '', count: 0 }] })}><Icon name="plus" /> Baumart</button></div>}
                      {(d.species?.length ?? 0) > 0 && <span className={`sum ${d.trees_planned != null && speciesSum !== d.trees_planned ? 'warn' : ''}`}>Summe {speciesSum.toLocaleString('de-DE')}{d.trees_planned != null ? ` von ${d.trees_planned.toLocaleString('de-DE')}` : ''}</span>}
                    </div>
                  </Field>
                  <T ctx={ctx} k="species_text" label="Baumarten als Text" full hint="falls nicht zeilenweise erfasst" />
                  <T ctx={ctx} k="planting_from" label="Pflanzung frühestens möglich ab" hint="z. B. November 2026" />
                  <T ctx={ctx} k="completion_until" label="Komplette Fertigstellung bis" />
                </div>
              </div>
            </section>
          )}
          {section === 'helfer' && (
            <section className="section">
              <div className="sh"><h2>Pflanzhelfer und Feuer</h2></div>
              <div className="sb"><div className="form">
                <JaNein ctx={ctx} k="helpers_by_forester" label="Pflanzhelfer durch Förster" />
                <N ctx={ctx} k="helpers_count" label="Anzahl Pflanzhelfer" />
                <JaNein ctx={ctx} k="fire_by_forester" label="Organisiert Förster ein Feuer" hint="Sonst Schwedenfackeln mitnehmen" />
              </div></div>
            </section>
          )}
          {section === 'plaene' && (
            <section className="section">
              <div className="sh"><h2>Bäume und Pläne</h2></div>
              <div className="sb"><div className="form">
                <Seg ctx={ctx} label="Bestellung erfolgt durch" value={d.tree_order_by} onChange={(v) => set({ tree_order_by: v as 'forstamt' | 'tww' | null })} options={[{ value: 'forstamt', label: 'Forstamt' }, { value: 'tww', label: 'TWW' }]} lg />
                <SegLookup ctx={ctx} k="remaining_planting_by" label="Restpflanzung / Fertigstellung durch" list="remaining_planting_by" />
                <JaNein ctx={ctx} k="planting_plan_by_forester" label="Pflanzplan liefert Förster" />
                <JaNein ctx={ctx} k="map_by_forester" label="Flächenkarte kommt vom Förster" />
              </div></div>
            </section>
          )}
          {section === 'vorbereitung' && (
            <section className="section">
              <div className="sh"><h2>Vorbereitung und Wildschutz</h2></div>
              <div className="sb"><div className="form">
                <SegLookup ctx={ctx} k="clearing" label="Flächenräumung" list="clearing_methods" />
                <SegLookup ctx={ctx} k="preparation" label="Flächenvorbereitung" list="preparation_methods" />
                <SegLookup ctx={ctx} k="planting_holes" label="Pflanzlochvorbereitung" list="planting_hole_methods" />
                <SegLookup ctx={ctx} k="wildlife_protection" label="Wildschutz" list="wildlife_protection" hint="Pflicht beim Abschluss, auch „kein Wildschutz“" />
                <TA ctx={ctx} k="wildlife_note" label="Vermerke" />
                <TA ctx={ctx} k="soil" label="Bodenverhältnisse" />
              </div></div>
            </section>
          )}
          {section === 'kosten' && (
            <section className="section">
              <div className="sh"><h2>Kostenvereinbarung</h2></div>
              <div className="sb"><div className="form">
                <NumberInput label="Maximal übernommener Betrag brutto pro Baum (€)" decimals={2} value={d.price_per_tree_max ?? null} onChange={(v) => set({ price_per_tree_max: v })} hint={priceHint} error={err('price_per_tree_max')} disabled={!write} />
                <TA ctx={ctx} k="cost_note" label="Anmerkungen zur Kostenvereinbarung" />
              </div></div>
            </section>
          )}
          {section === 'logistik' && (
            <section className="section">
              <div className="sh"><h2>Logistik</h2></div>
              <div className="sb"><div className="form">
                <T ctx={ctx} k="rescue_point" label="Rettungspunkt" />
                <JaNein ctx={ctx} k="meeting_point_ok" label="Sammelplatz vorhanden" hint="eben, ansprechend, fototauglich" />
                <TA ctx={ctx} k="bus_access" label="Anreise Bus" hint="Großbus, Mittelbus, Asphalt, Kurvenradius, Wendemöglichkeit, Parken" />
                <TA ctx={ctx} k="car_access" label="Anreise PKW" />
                <N ctx={ctx} k="parking_count" label="Anzahl Parkplätze" />
                <div className="geo-row full">
                  <TextInput label="Koordinaten Parken Teilnehmende" value={parking} onChange={(v) => { setParking(v); const c = parseCoords(v); set({ parking_lat: c?.lat ?? null, parking_lng: c?.lng ?? null }); }} placeholder="53.2431, 9.9605" disabled={!write} />
                  {write && <button type="button" className="btn" onClick={() => geo('parking')}><Icon name="map" /> Aktuellen Standort übernehmen</button>}
                </div>
                <T ctx={ctx} k="street_name" label="Straßenname zur Waldeinfahrt" />
                <TA ctx={ctx} k="signage" label="Ausschilderung" />
                <TA ctx={ctx} k="access_work" label="Zuwegung: Freischneiden oder Ausbesserung nötig" />
              </div></div>
            </section>
          )}
          {section === 'event' && (
            <section className="section">
              <div className="sh"><h2>Event und Absprachen</h2></div>
              <div className="sb"><div className="form">
                <TA ctx={ctx} k="press_contact" label="Kontakt zu Presse / Bürgermeister" />
                <TA ctx={ctx} k="caterer_suggestion" label="Vorschlag Caterer durch Förster" />
                <JaNein ctx={ctx} k="caterer_wc_space" label="Platz für Caterer und WC" />
                <TA ctx={ctx} k="agreements" label="Absprachen mit Gemeinde, Jagdpächtern, Nachbarn" />
                <TA ctx={ctx} k="drought_measures" label="Maßnahmen gegen Dürre- und Schadrisiko" />
                <SegLookup ctx={ctx} k="certification" label="Zertifizierung" list="certifications" />
                <TA ctx={ctx} k="other_notes" label="Sonstige Anmerkungen" />
              </div></div>
            </section>
          )}
          {section === 'abschluss' && (
            <section className="section">
              <div className="sh"><h2>Abschluss</h2><span className="desc">Unterschrift auf der Druckansicht</span></div>
              <div className="sb">
                {amend ? (
                  <>
                    <div className="form">
                      <DateInput label="Unterschrift Förster am" value={signed.signed_forester_at} onChange={(v) => setSigned((x) => ({ ...x, signed_forester_at: v || null }))} />
                      <DateInput label="Unterschrift TWW am" value={signed.signed_tww_at} onChange={(v) => setSigned((x) => ({ ...x, signed_tww_at: v || null }))} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                      <button type="button" className="btn primary" onClick={() => saveSigned.mutate()} disabled={!signedDirty || saveSigned.isPending}>
                        {saveSigned.isPending ? 'wird gespeichert …' : 'Speichern'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="form">
                    <DateInput label="Unterschrift Förster am" value={d.signed_forester_at} onChange={(v) => set({ signed_forester_at: v || null })} disabled={!write} />
                    <DateInput label="Unterschrift TWW am" value={d.signed_tww_at} onChange={(v) => set({ signed_tww_at: v || null })} disabled={!write} />
                  </div>
                )}
                {write && (
                  <>
                    {Object.keys(validation.errors).length > 0 ? (
                      <Note kind="bad">
                        Für den Abschluss fehlt noch:
                        <ul className="applied">{Object.values(validation.errors).map((e) => <li key={e}>{e}</li>)}</ul>
                      </Note>
                    ) : (
                      <Note kind="good">Alle Pflichtangaben sind vorhanden.</Note>
                    )}
                    {validation.warnings.length > 0 && <Note>{validation.warnings.join(' · ')}</Note>}
                  </>
                )}
              </div>
            </section>
          )}
          {section === 'fotos' && <PhotoSection visitId={visit.id} canWrite={can('areas', 'write')} />}
          {section !== 'fotos' && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {(() => {
                const i = SITE_VISIT_SECTIONS.findIndex((s) => s.key === section);
                const prev = SITE_VISIT_SECTIONS[i - 1];
                const next = SITE_VISIT_SECTIONS[i + 1];
                return (
                  <>
                    <button type="button" className="btn" disabled={!prev} onClick={() => prev && goSection(prev.key)}>Zurück</button>
                    <button type="button" className="btn" onClick={() => goSection(next ? next.key : 'fotos')}>{next ? `Weiter: ${next.title}` : 'Weiter: Fotos'}</button>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {finalizeOpen && (
        <Modal
          title="Protokoll abschließen"
          subtitle={visit.area.name}
          onClose={() => setFinalizeOpen(false)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setFinalizeOpen(false)}>Abbrechen</button>
              <button className="btn primary" type="button" disabled={Object.keys(validation.errors).length > 0 || finalize.isPending} onClick={() => finalize.mutate()}>Abschließen und übernehmen</button>
            </>
          }
        >
          {Object.keys(validation.errors).length > 0 ? (
            <Note kind="bad">Es fehlen noch: {Object.values(validation.errors).join(', ')}</Note>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>Beim Abschluss wird übernommen:</p>
              <ul className="applied">
                <li>Flächendaten (Ort, Flurstück, Koordinaten, Hektar, Waldbesitzer) in die Flächenakte; Fläche gilt als „Begangen“</li>
                {d.price_per_tree_max != null && <li>Baumpreis {fmtEur(d.price_per_tree_max)} in die Preishistorie des Försters{forester?.last_price && Math.abs(d.price_per_tree_max - forester.last_price.price) < 0.005 ? ' (unverändert, kein neuer Eintrag)' : ''}</li>}
                <li>Räumung, Vorbereitung, Pflanzloch, Wildschutz, Pflanzhelfer, Caterer, Presse, Bus {visit.action ? `in die Checkliste der Aktion ${visit.action.code}` : 'als Checklistenwerte am Protokoll, bis eine Aktion verknüpft ist'}</li>
              </ul>
              {validation.warnings.length > 0 && <Note>{validation.warnings.join(' · ')}</Note>}
              <p className="mut small">Danach sind nur noch die Unterschriftsdaten änderbar.</p>
            </>
          )}
        </Modal>
      )}
      {result && (
        <Modal title="Protokoll abgeschlossen" onClose={() => { setResult(null); nav(`/flaechen/${visit.area.id}`); }} footer={<button className="btn primary" type="button" onClick={() => { setResult(null); nav(`/flaechen/${visit.area.id}`); }}>Zur Flächenakte</button>}>
          <ul className="applied">{result.applied.map((a) => <li key={a}>{a}</li>)}</ul>
          {result.warnings.length > 0 && <Note>{result.warnings.join(' · ')}</Note>}
        </Modal>
      )}
    </>
  );
}

interface Ctx {
  d: Draft;
  set: (patch: Partial<Draft>) => void;
  write: boolean;
  opts: (key: string) => { value: string; label: string }[];
  validation: { errors: Record<string, string>; warnings: string[] };
}
/** Feld-Hilfskomponenten auf Modulebene, damit React sie nicht bei jedem Render neu montiert (Fokus bleibt erhalten) */
function Seg({ ctx, value, onChange, options, label, hint, error, lg }: { ctx: Ctx; value: string | null | undefined; onChange: (v: string | null) => void; options: { value: string; label: string }[]; label: string; hint?: string; error?: string; lg?: boolean }) {
  // lange Optionslisten bekommen die volle Zeile, damit der Schalter nicht umbricht
  const full = options.reduce((n, o) => n + o.label.length + 4, 0) > 34;
  return (
    <Field label={label} hint={hint} error={error} full={full}>
      <div className={`seg ${lg ? 'lg' : ''}`} role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={value === o.value ? 'on' : ''} onClick={() => ctx.write && onChange(value === o.value ? null : o.value)} disabled={!ctx.write}>
            {o.label}
          </button>
        ))}
      </div>
    </Field>
  );
}
function JaNein({ ctx, k, label, hint }: { ctx: Ctx; k: keyof Draft; label: string; hint?: string }) {
  const v = ctx.d[k];
  return <Seg ctx={ctx} label={label} hint={hint} value={v == null ? null : v ? 'ja' : 'nein'} onChange={(x) => ctx.set({ [k]: x == null ? null : x === 'ja' } as Partial<Draft>)} options={[{ value: 'ja', label: 'ja' }, { value: 'nein', label: 'nein' }]} lg />;
}
function SegLookup({ ctx, k, label, list, hint }: { ctx: Ctx; k: keyof Draft; label: string; list: string; hint?: string }) {
  const o = ctx.opts(list);
  const error = ctx.write && ctx.validation.errors[k as string] ? ctx.validation.errors[k as string] : undefined;
  return o.length <= 4 ? (
    <Seg ctx={ctx} label={label} hint={hint} value={ctx.d[k] as string} onChange={(v) => ctx.set({ [k]: v } as Partial<Draft>)} options={o} error={error} lg />
  ) : (
    <Select label={label} hint={hint} error={error} value={ctx.d[k] as string} onChange={(v) => ctx.set({ [k]: v || null } as Partial<Draft>)} options={o} disabled={!ctx.write} />
  );
}
function T({ ctx, k, label, hint, full }: { ctx: Ctx; k: keyof Draft; label: string; hint?: string; full?: boolean }) {
  return <TextInput full={full} label={label} hint={hint} value={ctx.d[k] as string} onChange={(v) => ctx.set({ [k]: v } as Partial<Draft>)} disabled={!ctx.write} />;
}
function TA({ ctx, k, label, hint }: { ctx: Ctx; k: keyof Draft; label: string; hint?: string }) {
  return <TextArea label={label} hint={hint} value={ctx.d[k] as string} onChange={(v) => ctx.set({ [k]: v } as Partial<Draft>)} rows={2} disabled={!ctx.write} />;
}
function N({ ctx, k, label, hint }: { ctx: Ctx; k: keyof Draft; label: string; hint?: string }) {
  return <NumberInput label={label} hint={hint} value={ctx.d[k] as number | null} onChange={(v) => ctx.set({ [k]: v } as Partial<Draft>)} disabled={!ctx.write} />;
}

/** Fotos der Begehung: Kamera oder Auswahl, mehrere, Vorschau; Art „Foto“, hängen am Protokoll */
function PhotoSection({ visitId, canWrite }: { visitId: number; canWrite: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['documents', 'site_visit', visitId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<{ items: DocumentDto[] }>(`/documents${qs({ entity_type: 'site_visit', entity_id: visitId })}`).then((r) => r.items) });
  const input = useRef<HTMLInputElement>(null);
  const up = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) {
        const fd = new FormData();
        fd.set('entity_type', 'site_visit');
        fd.set('entity_id', String(visitId));
        fd.set('kind', 'foto');
        fd.set('file', f);
        await api.upload('/documents', fd);
      }
    },
    onSuccess: (_d, files) => {
      qc.invalidateQueries({ queryKey: key });
      toast(`${files.length} Foto${files.length > 1 ? 's' : ''} angehängt`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const [confirmDel, setConfirmDel] = useState<DocumentDto | null>(null);
  const del = useMutation({
    mutationFn: (id: number) => api.post(`/documents/${id}/archive`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      setConfirmDel(null);
      toast('Foto entfernt');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <section className="section">
      {confirmDel && <ConfirmDialog title="Foto entfernen" text={<>„{confirmDel.original_name}“ wird aus dem Protokoll entfernt.</>} confirmLabel="Entfernen" danger onConfirm={() => del.mutate(confirmDel.id)} onClose={() => setConfirmDel(null)} busy={del.isPending} />}
      <div className="sh"><h2>Fotos der Begehung</h2></div>
      <div className="sb">
        {q.isLoading ? <Skeleton rows={2} /> : (
          <div className="photo-grid">
            {(q.data ?? []).map((d) => (
              <div key={d.id} style={{ position: 'relative' }}>
                <a href={`/api/v1/documents/${d.id}/file`} target="_blank" rel="noreferrer">
                  <img src={d.has_thumb ? `/api/v1/documents/${d.id}/thumb` : `/api/v1/documents/${d.id}/file`} alt={d.original_name} />
                </a>
                {canWrite && <button type="button" className="icon-btn" style={{ position: 'absolute', top: 2, right: 2, background: 'var(--surface)' }} aria-label="Foto entfernen" title="Foto entfernen" onClick={() => setConfirmDel(d)}><Icon name="x" /></button>}
              </div>
            ))}
            {canWrite && (
              <div className="add" role="button" tabIndex={0} onClick={() => input.current?.click()} onKeyDown={(e) => e.key === 'Enter' && input.current?.click()}>
                <span><Icon name="photo" /><br />{up.isPending ? 'wird hochgeladen …' : 'Foto aufnehmen oder auswählen'}</span>
              </div>
            )}
          </div>
        )}
        <input ref={input} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => e.target.files && up.mutate(Array.from(e.target.files))} />
      </div>
    </section>
  );
}
